import { section, p, list, steps, tip, warn, table, faq } from './blocks';

export default {
  slug: 'loyalty-kundenbindung',
  title: 'Loyalty & Kundenbindung',
  shortTitle: 'Loyalty',
  icon: 'Heart',
  updated: 'Juni 2026',
  datePublished: '2026-06-24',
  dateModified: '2026-06-24',
  image: 'https://images.unsplash.com/photo-1565718253569-3156836e2ec0?w=1200&h=900&fit=crop',
  teaser: 'Stammgäste sind dein wertvollstes Kapital. Wie digitale Stempelkarten, Punkteprogramme, Gutscheine und Gäste-CRM Besuchsfrequenz und Umsatz nachhaltig steigern.',
  mainArticle: {
    title: 'Loyalty und Kundenbindung digital: Der große Leitfaden',
    takeaways: [
      'Stammgäste sind der wirtschaftlichste Wachstumshebel – ein zusätzlicher Besuch pro Jahr trägt das Programm.',
      'Je weniger der Gast tun muss (Wallet-Karte, Kartenerkennung), desto höher die Teilnahme.',
      'Erste Belohnung schnell, Regeln in einem Satz erklärbar, Prämien zum Wareneinsatz kalkuliert.',
      'Gesammelte Daten sind nur wertvoll, wenn du sie für Reaktivierung und gezielte Ansprache nutzt.',
    ],
    intro: 'Einen neuen Gast zu gewinnen kostet ein Vielfaches davon, einen bestehenden zu halten. Trotzdem investieren die meisten Betriebe fast ihr gesamtes Marketingbudget in Neukunden. Digitale Kundenbindung dreht diese Logik um – und liefert dabei Daten, die du sonst nie bekommen würdest.',
    sections: [
      section(
        'Warum Stammgäste die wichtigste Zielgruppe sind',
        p('Ein Gast, der alle zwei Wochen kommt und 35 € ausgibt, bringt im Jahr rund 900 € Umsatz. Zehn solcher Gäste mehr sind 9.000 € – ohne einen Cent für Werbung. Stammgäste bestellen mehr, reklamieren weniger, empfehlen weiter und sind unempfindlicher gegen Preiserhöhungen. Kundenbindung ist deshalb kein „Nice-to-have“, sondern der wirtschaftlichste Wachstumshebel in der Gastronomie.'),
        p('Digitale Loyalty-Programme machen Kundenbindung **messbar und steuerbar**: Du siehst, wer wie oft kommt, wer länger nicht da war und welche Anreize wirken.')
      ),
      section(
        'Formen digitaler Kundenbindung',
        table(
          ['Form', 'Funktionsweise', 'Stärke', 'Typisch für'],
          [
            ['Digitale Stempelkarte', '10 Kaffee, der 11. gratis – auf dem Smartphone', 'Einfach, sofort verständlich', 'Café, Bäckerei, Imbiss'],
            ['Punkteprogramm', 'Punkte je Euro, einlösbar gegen Prämien', 'Flexibel, fördert höhere Bons', 'Restaurants, Ketten'],
            ['Cashback / Guthaben', 'Prozentsatz des Umsatzes als Guthaben für den nächsten Besuch', 'Klarer Wert, einfache Kommunikation', 'Systemgastro, Lieferkanal'],
            ['Mitgliedschaft / Abo', 'Monatsbeitrag für Vorteile (z. B. täglicher Kaffee, Rabatt)', 'Planbarer Umsatz, hohe Bindung', 'Café, Mittagstisch'],
            ['Gutscheine', 'Verkauf und digitale Einlösung', 'Vorab-Umsatz, Neukunden durch Beschenkte', 'Alle Konzepte'],
            ['Newsletter / CRM', 'Gezielte Ansprache nach Verhalten', 'Reaktivierung, Eventvermarktung', 'Alle Konzepte'],
          ]
        )
      ),
      section(
        'Wie ein digitales Loyalty-Programm technisch funktioniert',
        p('Jedes Programm braucht zwei Dinge: eine Möglichkeit, den Gast zu **identifizieren**, und eine Verbindung zur **Kasse**, damit Umsätze automatisch gutgeschrieben werden. Bei der Identifikation gibt es verschiedene Wege:'),
        list([
          '**Wallet-Karte:** Der Gast speichert eine digitale Karte im Smartphone-Wallet – kein App-Download, QR-Code wird an der Kasse gescannt',
          '**QR-Code auf dem Bon:** Gast scannt nach dem Bezahlen und sammelt nachträglich',
          '**Telefonnummer oder E-Mail:** Eingabe an der Kasse oder am Kiosk',
          '**Kartenbasiert:** Erkennung über die Zahlungskarte – vollständig ohne Interaktion',
          '**Eigene App:** Höchste Bindung, aber hohe Hürde – nur für Ketten mit großer Stammkundschaft sinnvoll',
        ]),
        tip('Je weniger der Gast tun muss, desto höher die Teilnahmequote. Wallet-Karten und kartenbasierte Erkennung erreichen in der Praxis deutlich mehr Gäste als eine eigene App.')
      ),
      section(
        'Das Programm gestalten',
        p('Ein gutes Loyalty-Programm ist einfach zu verstehen, schnell zu erreichen und passt zum Konzept. Entscheide zuerst, welches **Verhalten** du fördern willst: häufigere Besuche (Frequenz), höhere Bons oder Besuche zu bestimmten Zeiten (z. B. Dienstagabend).'),
        list([
          '**Erste Belohnung schnell:** Der erste Vorteil sollte spätestens beim zweiten Besuch kommen – sonst steigen Gäste aus',
          '**Klare Regel:** „Jeder 10. Kaffee gratis“ schlägt „3 % Punkte auf ausgewählte Produkte“',
          '**Belohnung mit Erlebniswert:** Ein Dessert oder ein exklusives Gericht bindet stärker als 2 € Rabatt',
          '**Überraschungen:** Unerwartete Geburtstagsgrüße oder ein Gratis-Getränk nach langer Abwesenheit wirken stärker als planbare Prämien',
          '**Kosten kalkulieren:** Prämien zu Wareneinsatz statt Verkaufspreis rechnen – ein Gratis-Kaffee kostet dich 0,40 €, nicht 3,50 €',
        ])
      ),
      section(
        'Datennutzung und Gäste-CRM',
        p('Mit jedem Scan entsteht ein Bild deiner Gäste: Besuchshäufigkeit, Lieblingszeiten, Bonhöhe, bevorzugte Produkte. Diese Daten sind die Grundlage für gezieltes Marketing statt Gießkanne:'),
        list([
          '**Reaktivierung:** Gäste, die seit 60 Tagen nicht da waren, erhalten eine persönliche Einladung',
          '**Segmentierung:** Mittagsgäste erfahren vom Abendangebot, Wochenendgäste von Events',
          '**Anlässe:** Geburtstag, Jahrestag der ersten Bestellung',
          '**Feedback:** Nach dem Besuch kurze Zufriedenheitsabfrage – Kritik landet bei dir statt in der öffentlichen Bewertung',
          '**Auswertung:** Welche Aktion hat wie viele Besuche ausgelöst? Was kostet ein reaktivierter Gast?',
        ])
      ),
      section(
        'Rechtliche Rahmenbedingungen',
        p('Loyalty-Programme verarbeiten personenbezogene Daten und benötigen eine klare Rechtsgrundlage. Für die Programmteilnahme selbst reicht in der Regel die Vertragsbeziehung („Teilnahmebedingungen“); für Newsletter und Werbung brauchst du eine ausdrückliche Einwilligung. Gutscheine unterliegen zudem eigenen Regeln zu Gültigkeit und Umsatzsteuer.'),
        warn('Formuliere Teilnahmebedingungen und Datenschutzhinweise verständlich und halte einen Auftragsverarbeitungsvertrag mit dem Anbieter bereit. Details findest du in den Vertiefungsartikeln zu Gutscheinen und Newsletter.')
      ),
      section(
        'Kosten und Wirtschaftlichkeit',
        p('Digitale Loyalty-Lösungen kosten je nach Umfang etwa 30 – 200 € pro Monat und Standort, häufig plus Einrichtungsgebühr. Hinzu kommen die Prämienkosten – gerechnet zum Wareneinsatz. Dem stehen Mehrbesuche gegenüber:'),
        table(
          ['Annahme', 'Wert'],
          [
            ['Aktive Programmteilnehmer', '400 Gäste'],
            ['Zusätzliche Besuche pro Teilnehmer und Jahr', '3'],
            ['Durchschnittsbon', '28 €'],
            ['Zusatzumsatz pro Jahr', '33.600 €'],
            ['Prämienkosten (Wareneinsatz) und Software', 'ca. 6.000 €'],
            ['**Zusätzlicher Rohertrag**', '**ca. 17.500 €** (bei 30 % Wareneinsatz auf den Zusatzumsatz)'],
          ]
        ),
        p('Die Zahlen variieren stark nach Konzept – aber schon ein zusätzlicher Besuch pro Teilnehmer und Jahr trägt die Kosten in den meisten Betrieben.')
      ),
      section(
        'Das richtige System finden',
        steps([
          '**Kassenanbindung:** Werden Umsätze automatisch gutgeschrieben oder muss das Personal manuell buchen?',
          '**Identifikationsweg:** Wallet, QR, Karte oder App – was passt zu deinen Gästen?',
          '**Programmlogik:** Stempel, Punkte, Cashback, Abo – ist das gewünschte Modell umsetzbar?',
          '**Kommunikation:** Sind Push-Nachrichten, E-Mail oder SMS integriert?',
          '**Auswertungen:** Teilnehmer, Aktivität, Einlösungen, Umsatzwirkung?',
          '**Datenhoheit:** Gehören die Gästedaten dir, exportierbar bei Anbieterwechsel?',
          '**Mehrfilialfähigkeit**, falls relevant.',
        ])
      ),
      section(
        'Warum Loyalty-Programme scheitern',
        list([
          '**Zu komplizierte Regeln:** Wenn das Team das Programm nicht in einem Satz erklären kann, funktioniert es nicht.',
          '**Belohnung zu weit weg:** 20 Stempel bis zur Prämie – die meisten Karten sterben bei fünf.',
          '**Team nicht eingebunden:** Ohne aktives Ansprechen an der Kasse bleibt die Teilnahme gering.',
          '**Daten sammeln, aber nicht nutzen:** Das Programm läuft, aber niemand wertet aus oder kommuniziert.',
          '**Rabatt statt Wertschätzung:** Reine Preisnachlässe ziehen Schnäppchenjäger, nicht Stammgäste.',
        ])
      ),
      section(
        'Fragen, die uns oft gestellt werden',
        faq([
          { q: 'Funktionieren Loyalty-Programme auch in der gehobenen Gastronomie?', a: 'Ja, aber anders: Statt Stempelkarten stehen persönliche Wertschätzung, exklusive Einladungen, Vorab-Reservierungsrechte oder ein Küchengruß im Vordergrund. Die Technik dahinter – Gästeprofil und gezielte Kommunikation – ist dieselbe.' },
          { q: 'Wie hoch ist eine realistische Teilnahmequote?', a: 'Bei aktivem Ansprechen an der Kasse und niedriger Hürde (Wallet-Karte, kein App-Download) erreichen Betriebe 20 – 40 % ihrer regelmäßigen Gäste innerhalb weniger Monate. Mit App-Pflicht liegt die Quote meist deutlich niedriger.' },
          { q: 'Kann ich das Programm mit dem Lieferkanal verbinden?', a: 'Im eigenen Bestellkanal ja – das ist sogar ein starkes Argument, Gäste von Plattformen in den eigenen Shop zu holen. Auf Lieferplattformen selbst ist eine Anbindung in der Regel nicht möglich.' },
          { q: 'Was ist mit der Papier-Stempelkarte?', a: 'Sie funktioniert weiterhin, liefert aber keine Daten, geht verloren und lässt sich leicht fälschen. Die digitale Variante kostet wenig mehr und bringt Auswertungen und Kommunikationsmöglichkeiten.' },
        ])
      ),
    ],
  },
  subArticles: [
    {
      slug: 'stempelkarte-punkte-cashback',
      title: 'Digitale Stempelkarte, Punkteprogramm oder Cashback: Welches Modell passt?',
      shortTitle: 'Stempel, Punkte oder Cashback',
      teaser: 'Die drei gängigsten Loyalty-Modelle im direkten Vergleich – mit Empfehlungen je Konzept und den psychologischen Effekten, die du nutzen kannst.',
      intro: 'Die Wahl des Modells entscheidet, ob dein Programm verstanden, genutzt und weiterempfohlen wird. Es gibt kein „bestes“ Modell – aber für jedes Konzept ein passendes.',
      sections: [
        section(
          'Die drei Modelle im Vergleich',
          table(
            ['Kriterium', 'Stempelkarte', 'Punkteprogramm', 'Cashback / Guthaben'],
            [
              ['Verständlichkeit', 'Sehr hoch', 'Mittel', 'Hoch'],
              ['Fördert', 'Besuchsfrequenz', 'Bonhöhe und Frequenz', 'Wiederkehr, höhere Bons'],
              ['Flexibilität', 'Gering (ein Produkt)', 'Hoch (viele Prämien)', 'Hoch (Guthaben frei einsetzbar)'],
              ['Aufwand Pflege', 'Gering', 'Mittel (Prämienkatalog)', 'Gering'],
              ['Kosten planbar', 'Ja', 'Mittel', 'Ja (fester Prozentsatz)'],
              ['Passt zu', 'Café, Bäckerei, Imbiss, Mittagstisch', 'Restaurant, Bar, Ketten', 'Systemgastro, Lieferkanal, Kiosk'],
            ]
          )
        ),
        section(
          'Stempelkarte: Einfachheit gewinnt',
          p('Die digitale Stempelkarte ist das Modell mit der höchsten Akzeptanz, weil jeder Gast sie sofort versteht. Sie eignet sich für Konzepte mit einem klaren Kernprodukt und hoher Wiederholungsfrequenz. Wichtig: Die Anzahl der Stempel bis zur Prämie sollte in **vier bis acht Wochen** erreichbar sein – bei täglichen Kaffeetrinkern sind 10 Stempel richtig, bei einem Restaurant mit zweiwöchentlichem Besuch eher 5.'),
          tip('Nutze den „Endowed Progress“-Effekt: Starte jede neue Karte mit zwei bereits gesetzten Stempeln. Gäste, die schon Fortschritt sehen, schließen die Karte deutlich häufiger ab.')
        ),
        section(
          'Punkteprogramm: Flexibel, aber erklärungsbedürftig',
          p('Punkte pro Euro belohnen höhere Bons und lassen sich gegen verschiedene Prämien einlösen. Damit steuerst du gezielt: Doppelte Punkte am Dienstagabend füllen den schwachen Tag, Punkte auf Desserts steigern den Bon. Der Nachteil: Der Gast muss wissen, was ein Punkt wert ist. Halte die Umrechnung einfach („1 € = 1 Punkt, 100 Punkte = 10 € Prämie“) und die Prämienliste kurz.')
        ),
        section(
          'Cashback und Guthaben: Klarer Wert',
          p('Bei Cashback erhält der Gast einen festen Prozentsatz (z. B. 5 %) seines Umsatzes als Guthaben für den nächsten Besuch. Der Wert ist sofort verständlich, das Guthaben liegt beim Gast – und verfällt, wenn er nicht wiederkommt. Das Modell eignet sich besonders für Selbstbedienung, Kioske und den eigenen Lieferkanal, wo die Gutschrift automatisch bei der Zahlung erfolgt. Achte auf die steuerliche Behandlung des Guthabens und eine klare Verfallsregel.')
        ),
        section(
          'Entscheidungshilfe',
          steps([
            'Welches Verhalten willst du fördern – häufiger kommen, mehr ausgeben oder zu bestimmten Zeiten kommen?',
            'Hast du ein Kernprodukt, das sich als Stempel eignet?',
            'Kann dein Team das Modell in einem Satz erklären?',
            'Ist die erste Belohnung innerhalb von zwei bis drei Besuchen erreichbar?',
            'Sind die Prämienkosten zum Wareneinsatz kalkuliert und tragbar?',
          ]),
          p('Im Zweifel: Starte mit der Stempelkarte. Sie ist am schnellsten eingeführt und lässt sich später in ein Punktemodell überführen, wenn deine Gäste das Programm angenommen haben.')
        ),
      ],
    },
    {
      slug: 'digitale-gutscheine',
      title: 'Digitale Gutscheine: Verkauf, Einlösung und Buchhaltung richtig organisieren',
      shortTitle: 'Digitale Gutscheine',
      teaser: 'Gutscheine bringen Vorab-Umsatz und neue Gäste. Wie du sie online verkaufst, sicher einlöst und steuerlich sauber verbuchst.',
      intro: 'Gutscheine sind das älteste Marketinginstrument der Gastronomie – und digital deutlich wirksamer: rund um die Uhr verkaufbar, fälschungssicher, automatisch verbucht. Dieser Artikel zeigt, worauf es bei Verkauf, Einlösung und Buchhaltung ankommt.',
      sections: [
        section(
          'Verkaufskanäle für Gutscheine',
          list([
            '**Online-Shop auf der Website:** Der Gast wählt Betrag oder Erlebnis, bezahlt und erhält den Gutschein sofort als PDF oder Link – ideal für Last-Minute-Geschenke',
            '**Vor Ort an der Kasse:** Verkauf als Artikel mit Code, gedruckt oder digital',
            '**Über Reservierungs- oder Loyalty-System:** Wenn die Anbieter Gutscheinmodule integrieren',
            '**Aktionen:** Saisonale Gutscheine (Weihnachten, Muttertag), Erlebnisgutscheine (Menü für zwei, Weinprobe), Firmenkunden',
          ]),
          p('Der Dezember ist in vielen Betrieben der umsatzstärkste Gutscheinmonat – ein Online-Verkauf ohne Öffnungszeiten holt hier die Käufer ab, die am 23. Dezember um 22 Uhr noch ein Geschenk suchen.')
        ),
        section(
          'Einlösung und Verwaltung',
          p('Jeder Gutschein braucht einen eindeutigen Code oder QR-Code, der bei der Einlösung geprüft und entwertet wird. Die Einlösung sollte direkt in der Kasse erfolgen, damit Restwerte, Teileinlösungen und Umsätze korrekt verbucht werden. Wichtige Funktionen:'),
          list([
            'Prüfung auf Gültigkeit und Restbetrag in Echtzeit',
            'Teileinlösung mit verbleibendem Guthaben',
            'Übersicht aller ausstehenden Gutscheine (Verbindlichkeiten!)',
            'Sperren bei Verlust oder Missbrauch',
            'Mehrfilialfähigkeit: In Standort A gekauft, in Standort B eingelöst',
          ])
        ),
        section(
          'Buchhaltung und Steuern',
          p('Ein verkaufter Gutschein ist zunächst kein Umsatz, sondern eine **Verbindlichkeit** – du schuldest dem Gast eine Leistung. Umsatz entsteht bei der Einlösung. Steuerlich wird zwischen zwei Arten unterschieden:'),
          table(
            ['', 'Einzweck-Gutschein', 'Mehrzweck-Gutschein'],
            [
              ['Definition', 'Leistung und Steuersatz stehen bei Verkauf fest (z. B. „Menü für zwei“)', 'Wertgutschein, einlösbar für Speisen (7 % / 19 %) und Getränke (19 %)'],
              ['Umsatzsteuer', 'Bereits beim Verkauf', 'Erst bei Einlösung'],
              ['Typisch', 'Erlebnisgutscheine', 'Wertgutscheine über einen Betrag'],
            ]
          ),
          warn('Gutscheine verjähren gesetzlich in der Regel nach drei Jahren zum Jahresende; kürzere Fristen sind nur mit sachlichem Grund zulässig. Nicht eingelöste Gutscheine werden nach Verjährung ertragswirksam. Kläre die Behandlung mit deinem Steuerberater und halte eine saubere Liste aller offenen Gutscheine.')
        ),
        section(
          'Gutscheine als Marketinginstrument',
          list([
            'Beschenkte sind oft Neukunden – ein guter erster Besuch macht sie zu Stammgästen',
            'Erlebnisgutscheine mit höherem Wert (Menü, Verkostung) haben höhere Bons als Wertgutscheine',
            'Eingelöste Gutscheine bringen im Schnitt zusätzlichen Umsatz über den Gutscheinwert hinaus',
            'Gutschein-Käufer sind Bestandsgäste, die dich weiterempfehlen – ein Dankeschön lohnt sich',
            'Firmen als Zielgruppe: Mitarbeitergeschenke, Weihnachtsfeiern, Sachbezugsgrenzen nutzen',
          ]),
          tip('Verknüpfe Online-Gutscheinshop, Website und Social-Media-Profile. Ein prominenter „Gutschein verschenken“-Button auf der Startseite ist besonders von November bis Dezember einer der wertvollsten Links deiner Website.')
        ),
        section(
          'Checkliste für den Start',
          steps([
            'Gutscheinarten festlegen (Wert, Erlebnis) und steuerlich einordnen lassen',
            'Verkaufskanal einrichten (Online + Kasse) mit eindeutigen Codes',
            'Einlösung in der Kasse testen inkl. Teileinlösung',
            'Verbindlichkeiten-Liste und monatlichen Abgleich mit dem Steuerberater vereinbaren',
            'AGB für Gutscheine (Gültigkeit, Übertragbarkeit, keine Barauszahlung) formulieren',
            'Gestaltung: Gutschein-PDF mit Logo, Anlass-Motiven, persönlicher Nachricht',
          ])
        ),
      ],
    },
    {
      slug: 'newsletter-gaeste-crm',
      title: 'Newsletter und Gäste-CRM: Stammgäste gezielt ansprechen',
      shortTitle: 'Newsletter & Gäste-CRM',
      teaser: 'Wie du rechtssicher Gästedaten sammelst, sinnvoll segmentierst und mit wenig Aufwand Kommunikation aufbaust, die Besuche auslöst statt zu nerven.',
      intro: 'Der eigene Verteiler ist der einzige Marketingkanal, den dir niemand wegnehmen kann – keine Algorithmusänderung, keine Plattformgebühr. Er lohnt sich aber nur, wenn die Inhalte relevant sind und die Daten sauber gesammelt wurden.',
      sections: [
        section(
          'Wo Gästedaten entstehen',
          p('Du musst keine Karteikarten ausfüllen lassen – Gästedaten entstehen an vielen Stellen deines Betriebs ohnehin. Die Kunst ist, sie zusammenzuführen und die Einwilligung für Marketing einzuholen:'),
          list([
            '**Reservierungssystem:** Name, Kontakt, Besuchshistorie – mit optionaler Newsletter-Zustimmung bei der Buchung',
            '**Loyalty-Programm:** Teilnehmer geben Kontaktdaten bei Anmeldung an',
            '**Eigener Bestellkanal:** Jede Online-Bestellung hinterlässt E-Mail und Bestellhistorie',
            '**Gäste-WLAN:** Anmeldung mit E-Mail und optionaler Zustimmung',
            '**Gutscheinshop:** Käufer- und Beschenktendaten',
            '**Website:** Newsletter-Anmeldung mit klarem Vorteil („Erfahre zuerst von unseren Events“)',
          ])
        ),
        section(
          'Rechtssicher sammeln: Double-Opt-In',
          p('Werbliche E-Mails brauchen eine ausdrückliche, dokumentierte Einwilligung. Der Standard ist das **Double-Opt-In**: Der Gast trägt sich ein und bestätigt per Klick in einer E-Mail. Die Einwilligung darf nicht vorangekreuzt und nicht Bedingung für eine andere Leistung sein. Jede E-Mail enthält einen Abmeldelink. Halte fest, wann und wo die Einwilligung erteilt wurde – bei einer Beschwerde musst du das nachweisen.'),
          warn('WhatsApp-Verteiler und SMS-Werbung unterliegen denselben Regeln – ohne dokumentierte Einwilligung sind sie unzulässig und werden zunehmend abgemahnt.')
        ),
        section(
          'Segmentierung statt Gießkanne',
          p('Eine Nachricht an alle ist der sicherste Weg zu Abmeldungen. Mit wenigen Segmenten wird Kommunikation relevant:'),
          table(
            ['Segment', 'Definition', 'Passende Inhalte'],
            [
              ['Stammgäste', 'Mehr als 6 Besuche pro Jahr', 'Exklusive Einladungen, Vorab-Reservierung, Dankeschön'],
              ['Schlummernde Gäste', 'Letzter Besuch vor mehr als 60 – 90 Tagen', 'Persönliche Einladung mit Anreiz'],
              ['Mittagsgäste', 'Besuche überwiegend 11 – 14 Uhr', 'Abendangebot, Wochenendbrunch'],
              ['Eventinteressierte', 'Haben an Events teilgenommen', 'Neue Veranstaltungen, Menüabende'],
              ['Gutscheinkäufer', 'Haben Gutscheine gekauft', 'Saisonale Gutscheinaktionen'],
            ]
          )
        ),
        section(
          'Inhalte und Frequenz',
          list([
            'Ein bis zwei Aussendungen pro Monat reichen – Regelmäßigkeit schlägt Häufigkeit',
            'Ein Thema pro Mail: neues Menü, Event, Saisonstart, persönliche Geschichte aus dem Betrieb',
            'Klare Handlung: Reservieren, Gutschein kaufen, Event buchen – mit einem Klick',
            'Persönliche Ansprache mit Namen, Absender ist eine Person, nicht „Team“',
            'Mobil optimiert – der Großteil wird auf dem Smartphone gelesen',
          ]),
          tip('Automatisiere die wichtigsten Anlässe: Willkommensmail nach Anmeldung, Geburtstagsgruß, Reaktivierung nach 90 Tagen. Diese drei Automationen laufen ohne Aufwand und bringen mehr Besuche als jede manuelle Aussendung.')
        ),
        section(
          'Erfolg messen',
          list([
            'Öffnungs- und Klickrate je Aussendung (Orientierung: 30 – 50 % Öffnung bei Gastro-Newslettern)',
            'Reservierungen und Gutscheinkäufe über den Newsletter-Link',
            'Einlösungen von Codes aus der Aussendung an der Kasse',
            'Abmelderate (unter 0,5 % pro Aussendung ist gut)',
            'Wachstum des Verteilers pro Monat und je Quelle',
          ])
        ),
      ],
    },
  ],
};
